import { type ReactNode, useRef, useState } from "react";
import { type RouteComponentProps, withRouter } from "react-router";
import { Key } from "ts-key-enum";

import { BTPopover } from "@btnet/core-components/BTPopover";
import { cn } from "@btnet/utilities-core";

import { useTracking } from "utilities/analytics/analytics";
import { type IEditModeLink, isIEditModeRoute } from "utilities/editMode/edit.utility";
import { isRouteLocationState } from "utilities/flatRoutes/routes";
import { type IRouteLocationState } from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation.types";

import {
    BTIconCheckCircleOutlinedSuccess,
    BTIconCloseOutlined,
    BTIconDailyLogs,
} from "commonComponents/btWrappers/BTIcon";
import { BTLightbox } from "commonComponents/btWrappers/BTLightbox/BTLightbox";
import { mediaDataToSlide } from "commonComponents/btWrappers/BTLightbox/BTLightbox.utility";
import { getRelatedItemEntityData } from "commonComponents/entity/relatedItem/RelatedItem.utilities";
import { MediaModalType } from "commonComponents/utilities/MediaModal/common/types";
import { type IMediaData } from "commonComponents/utilities/MediaModal/common/types";

import "./RelatedItemTag.less";

import { RelatedItemType } from "../RelatedItem.types";

export interface IRelatedItemTagProps extends RouteComponentProps {
    className?: string;
    "data-testid"?: string;
    /**
     * This will prevent the content item entity type label from showing in the tag
     * before the subject text.
     * @default false
     */
    hideEntityTypeLabel?: boolean;
    /**
     * Overrides the entity-type label shown before the subject that is derived from the itemType.
     * Use this when a caller needs a more specific noun than the shared type mapping provides (e.g. labeling a
     * bill-typed related item as "Vendor Credit") without changing the shared mapping.
     */
    entityTypeLabelOverride?: string;
    /**
     * If there is an action already associated with the tag
     * and you need to toggle the ability of that action.
     * @default false
     */
    isDisabled?: boolean;
    /**
     * If the `to` prop is a full url to the related item set this to true.
     * @default false
     */
    isToFullUrl?: boolean;
    /**
     * This ia a matching enum for RelatedItemType and specifies
     * what related item entity type this is. Defaults to 0 (None).
     * @default RelatedItemType.None
     */
    itemType?: RelatedItemType;
    /**
     * This will prevent the content from wrapping and make
     * the tag fit the full width of it's parent. Code is in place
     * to add a popover if the content is truncated.
     * @default false
     */
    noWrapping?: boolean;
    /**
     * This will prevent the tag from using a min-width for the tag size.
     * @default false
     */
    noMinWidth?: boolean;
    /**
     * Handler that provides the ability to run extra code upon
     * being initiated. Can toggle with `isDisabled` prop.
     */
    onAction?: () => void;
    /**
     * Use with the `onAction` prop to have calling component handle the click event and prevent RelatedItemTag component from navigating
     */
    shouldPreventNavigation?: boolean;
    /**
     * This will show the "x" and allow use to remove the tag
     * @default true
     */
    canRemove?: boolean;
    /**
     * Function that provides the ability to run extra code upon
     * being initiated. Close icon only shows when this is passed in.
     */
    onRemove?: () => void;
    /**
     * This will be a list of rolled up RelatedItemsTags that will display
     * in a popover as a list menu to select additional Related Items
     */
    popRollupContent?: React.ReactNode;
    /**
     * Pass in a status ReactNode component to display the status.
     */
    status?: ReactNode;
    /**
     * The subject property is the main text of the RelatedItemTag
     * @required
     */
    subject: React.ReactNode;
    /**
     * Provide a link address to the related item either relative or full. Use the `isToFullUrl` prop
     * to handle the full url correctly.
     */
    to?: string | IRouteLocationState | IEditModeLink;
    /**
     * For use when you know you are doing a display of multiple tags next to each other.
     * This is for a common display. If you need unique padding or styling manage that from the
     * parent styles and pass in a special class name from that parent component.
     * @default false
     */
    useMultiTagMargins?: boolean;
    /**
     * Overrides the Icon to be a green checkmark and adds a strikethrough
     */
    isComplete?: boolean;

    allowLabelOverflowAndWrap?: boolean;

    amount?: ReactNode;

    /**
     * Is the entity in View State?
     * @required
     */
    isInViewState?: boolean;

    /**
     * Optional trailing action slot rendered inside the tag, after the
     * subject/status/amount and before the remove icon. Click and key
     * events bubble through, so the action node is responsible for
     * stopping propagation if it shouldn't trigger the tag's onAction.
     */
    actions?: ReactNode;
}

/**
 * RelatedItemTag can match the many entity types in the application to be a tag on one entity to
 * show the relationship between the two or many entities. This can be display only or actionable to
 * be able to being up the entity for view or editing. It provides several props for adjusting the display
 * and providing actions along with route linking.
 */
const RelatedItemTagInternal: React.FunctionComponent<IRelatedItemTagProps> = ({
    className,
    "data-testid": dataTestId = "related-item-tag",
    isDisabled = false,
    isToFullUrl = false,
    itemType = RelatedItemType.None,
    canRemove = true,
    onRemove,
    onAction,
    shouldPreventNavigation = false,
    popRollupContent,
    noWrapping = false,
    noMinWidth = false,
    hideEntityTypeLabel = false,
    entityTypeLabelOverride,
    status,
    subject,
    to,
    useMultiTagMargins = false,
    history,
    match,
    isComplete,
    allowLabelOverflowAndWrap,
    amount,
    isInViewState = false,
    actions,
}) => {
    const refSubject = useRef<HTMLDivElement>(null);
    const refRelatedItemTag = useRef<HTMLDivElement>(null);
    const refRemove = useRef<HTMLDivElement>(null);
    const [openMediaModal, setOpenMediaModal] = useState(false);
    const [isPopTriggered, setIsPopHovered] = useState(false);
    const [isPopFocused, setIsPopFocused] = useState(false);
    const { trackEvent } = useTracking();
    /**
     * Get the media data config structure needed for the
     * media modal if the related item is an image or video
     */
    const getMediaData = () => {
        let mediaModalType: MediaModalType;
        switch (itemType) {
            case RelatedItemType.PhotoDoc:
                mediaModalType = MediaModalType.image;
                break;
            case RelatedItemType.Video:
                mediaModalType = MediaModalType.video;
                break;
            default:
                mediaModalType = MediaModalType.image;
                break;
        }
        const data: IMediaData[] = [
            {
                fileName: "",
                source: "",
                type: mediaModalType,
            },
        ];

        if (to) {
            const srcUrl = isRouteLocationState(to) || isIEditModeRoute(to) ? to.pathname : to;
            data[0].source = isToFullUrl ? srcUrl : `${match.url}${srcUrl}`;
        }
        return data;
    };

    /**
     * This will place the tag in the standard tab index flow of the DOM
     * if this tag is actionable.
     */
    const getTabIndex = () => {
        return (onAction || to) && !isDisabled ? 0 : undefined;
    };

    /**
     * This handler will run the passed in onRemove handler from the parent. The parent is responsible for
     * how the remove is handled.
     */
    const handleRemove = (event: React.MouseEvent | React.KeyboardEvent) => {
        event.stopPropagation();
        if (onRemove) {
            onRemove();
        }
    };

    /**
     * This handler will run the passed in `onAction` handler from the parent for logic that needs to run base on the action.
     * It will also handle the linking with a `to` property and the different linking styles needed between the different Entity types.
     * Will prevent routing to the related entity if `shouldPreventNavigation` is true
     */
    const handleContainerAction = (event: React.MouseEvent | React.KeyboardEvent) => {
        if (!isDisabled) {
            event.stopPropagation();

            trackEvent({
                event: "LinkClick",
                component: "RelatedItemTag",
                uniqueId: dataTestId,
                extraInfo: {
                    itemType: itemType,
                },
            });

            if (onAction) {
                onAction();
                if (shouldPreventNavigation) {
                    return;
                }
            }

            if (to) {
                if (isRouteLocationState(to) || isIEditModeRoute(to)) {
                    history.push(to);
                } else {
                    switch (itemType) {
                        case RelatedItemType.PhotoDoc:
                        case RelatedItemType.Video:
                            setOpenMediaModal(true);
                            return;
                        case RelatedItemType.Document:
                            window.location.assign(isToFullUrl ? to : `${match.url}${to}`);
                            return;
                        case RelatedItemType.Takeoff:
                            window.open(to, "_blank");
                            return;
                        default:
                            history.push(isToFullUrl ? to : `${match.url}${to}`);
                            return;
                    }
                }
            }
        }
    };

    /**
     * This handler will determine if the content is truncated and will only investigate this if
     * the `noWrapping` prop is set to true.
     */
    const isTruncated = () => {
        if (noWrapping) {
            const subjectEl = refSubject.current;
            return subjectEl && subjectEl.offsetWidth < subjectEl.scrollWidth;
        }
        return false;
    };

    /**
     * Antdesign stated that this must be the approach of handinling multiple popovers
     * that trigger from different trigger actions. The need for state function handelers and
     * nested popover components. I'm sure there is a better way and this will hopefully get improved
     * with a BTPoper refactor.
     */
    /**
     * Set all popover states to be false and close or prevent popovers from showing
     */
    const popHide = () => {
        setIsPopHovered(false);
        setIsPopFocused(false);
    };
    /**
     * Handler to manage tooltip for truncated subject text or to handle
     * popover menu when using popRollupMenu.
     * This is to manage keyboard and click focus functionality
     */
    const popHandleFocused = (visible: boolean) => {
        setIsPopHovered(false);

        // set the pop focus state only when subject is truncated or we have popRollupMenu
        if (isTruncated() || popRollupContent) {
            setIsPopFocused(visible);
        } else {
            // don't use tooltip if text is not truncated or have menu popRollupContent
            popHide();
        }
    };

    /**
     * Handler to manage tooltip for truncated subject text.
     * This is to manage mouse hover functionality
     */
    const popHandleHovered = (visible: boolean) => {
        if (!popRollupContent) {
            if (isTruncated()) {
                setIsPopHovered(visible);
                setIsPopFocused(false);
            } else {
                popHide();
            }
        }
    };

    /**
     * Grab the Related Item entity type object with icon and label info by enum
     */
    const entityData = getRelatedItemEntityData(itemType);
    const entityTypeLabel = entityTypeLabelOverride ?? entityData.name;
    // on flag cleanup, make sure this function returns proper icon

    const getRelatedIcon = () => {
        if (isComplete) {
            return BTIconCheckCircleOutlinedSuccess;
        }

        return itemType === RelatedItemType.DailyLog ? BTIconDailyLogs : entityData.RelatedIcon;
    };

    const RelatedIcon = getRelatedIcon();

    const relatedItem = (
        <BTPopover
            content={popRollupContent ? popRollupContent : subject}
            trigger="focus"
            placement={popRollupContent ? "right" : "bottom"}
            visible={isPopFocused}
            onVisibleChange={popHandleFocused}
            destroyTooltipOnHide={{ keepParent: false }}
            widthType="unrestricted"
        >
            <BTPopover
                content={subject}
                trigger="hover"
                placement="bottom"
                visible={isPopTriggered}
                onVisibleChange={popHandleHovered}
                destroyTooltipOnHide={{ keepParent: false }}
            >
                <div
                    role="button"
                    data-testid={dataTestId}
                    className={cn("RelatedItemTag", className, {
                        "RelatedItemTag-actionable": (onAction || to) && !isDisabled,
                        "RelatedItemTag-disabled": (onAction || to) && isDisabled,
                        "RelatedItemTag-no-wrapping": noWrapping,
                        "RelatedItemTag-no-min-width": noMinWidth,
                        "RelatedItemTag-multi-tag": useMultiTagMargins,
                        completed: isComplete,
                    })}
                    ref={refRelatedItemTag}
                    tabIndex={getTabIndex()}
                    onClick={handleContainerAction}
                    onKeyUp={(event) => {
                        if (event.key === Key.Enter) {
                            handleContainerAction(event);
                        }
                    }}
                >
                    {itemType !== undefined && itemType !== RelatedItemType.None && (
                        <>
                            <RelatedIcon className="RelatedItemTag--icon" title={entityTypeLabel} />
                            {!hideEntityTypeLabel && (
                                <div
                                    className={cn("RelatedItemTag--label", {
                                        "RelatedItemTag--label--overflow-and-wrap-fix":
                                            allowLabelOverflowAndWrap === true,
                                    })}
                                >
                                    {entityTypeLabel}:
                                </div>
                            )}
                        </>
                    )}

                    <div className={cn("RelatedItemTag--subject")} ref={refSubject}>
                        {subject}
                    </div>

                    {status && <div className="RelatedItemTag--status">{status}</div>}
                    {amount && (
                        <div data-testid="related-item-amount" className="RelatedItemTag--amount">
                            {amount}
                        </div>
                    )}
                    {actions && (
                        <div
                            className="RelatedItemTag--actions"
                            // The tag's outer div is the navigation target — stop clicks
                            // and key events on the action slot from bubbling up so a
                            // dropdown/menu button inside doesn't also trigger onAction.
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            onKeyUp={(e) => e.stopPropagation()}
                            role="presentation"
                        >
                            {actions}
                        </div>
                    )}
                    {!isInViewState && canRemove && onRemove && (
                        <div
                            className="RelatedItemTag--remove"
                            ref={refRemove}
                            tabIndex={0}
                            onClick={handleRemove}
                            onKeyUp={(event: React.KeyboardEvent<Element>) => {
                                if (event.key === Key.Enter) {
                                    handleRemove(event);
                                }
                            }}
                        >
                            <BTIconCloseOutlined
                                className="RelatedItemTag--remove--icon"
                                size={10}
                                aria-label={`Remove ${entityTypeLabel}: ${subject}`}
                                data-testid={`remove-${entityTypeLabel}-${subject}`}
                            />
                        </div>
                    )}
                </div>
            </BTPopover>
        </BTPopover>
    );

    /**
     * Return the relatedItem and add the MediaModal to handle the cases where the entity
     * is a media item.
     */
    return (
        <>
            {relatedItem}
            <BTLightbox
                slides={getMediaData().map(mediaDataToSlide)}
                open={openMediaModal}
                onClose={() => {
                    setOpenMediaModal(false);
                }}
            />
        </>
    );
};

/**
 * Add the withRouter to handle the routing props
 */
export const RelatedItemTag = withRouter(RelatedItemTagInternal);
